import React, { useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useGame } from '../context/GameContext';
import { markRevealSeen } from '../services/api';
import ScoreDisplay from '../components/ScoreDisplay';
import RevealPoint from '../components/RevealPoint';
import { getQuestions } from '../data/getQuestions';

const Result: React.FC = () => {
  const { sessionId, triviaScore, triviaAnswers, participantData } = useGame();
  const revealSection = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('result');
  const questions = getQuestions(t);

  useEffect(() => {
    if (sessionId) {
      markRevealSeen(sessionId);
    }
  }, [sessionId]);

  const scrollToReveal = () => {
    revealSection.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Get Q3 (meta question) answer for dynamic reveal point
  const q3Answer = triviaAnswers.find((a) => a.question_id === 3);
  const q3Correct = q3Answer?.correct ?? false;

  const reveal3Emoji = q3Correct ? '🟡' : '🔴';
  const reveal3Title = q3Correct
    ? t('analysis.q3Correct.title')
    : t('analysis.q3Incorrect.title');
  const reveal3Description = q3Correct
    ? t('analysis.q3Correct.description')
    : t('analysis.q3Incorrect.description');

  const nombre = participantData?.nombre_completo || t('fallbackName');
  const email = participantData?.email || t('fallbackEmail');

  // Stand number is set via env at build time; until it's assigned, the copy
  // renders without the parenthesis (e.g. "pasa por nuestro stand y ...")
  const standNumber = process.env.REACT_APP_STAND_NUMBER;
  const stand = standNumber ? ` (${standNumber})` : '';

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(135deg, #00082f 0%, #001055 60%, #00082f 100%)' }}
    >
      {/* Section 1: Score */}
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm flex flex-col gap-8">
          <ScoreDisplay score={triviaScore} total={questions.length} />
          <button
            onClick={scrollToReveal}
            className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-lg rounded-2xl px-8 min-h-[56px] py-4 transition-all active:scale-[0.98] shadow-lg shadow-yellow-500/30"
          >
            {t('discover')}
          </button>
        </div>
      </div>

      {/* Section 2: Plot Twist */}
      <div ref={revealSection} className="px-6 py-12">
        <div className="w-full max-w-sm mx-auto">
          <div className="dramatic-pause glass-card p-6 border-yellow-500/30 text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-yellow-300 mb-4">{t('twist.title')}</h2>
            <p className="text-white text-base leading-relaxed">
              <strong>{t('twist.noIphone')}</strong> {t('twist.noRaffle')}
            </p>
            <p className="text-white/80 text-base leading-relaxed mt-3">
              <Trans i18nKey="twist.explanation" ns="result" components={{ 1: <strong className="text-yellow-300" /> }} />
            </p>
          </div>
        </div>
      </div>

      {/* Section 3: Analysis */}
      <div className="px-6 pb-8">
        <div className="w-full max-w-sm mx-auto flex flex-col gap-4">
          <h3 className="text-white font-bold text-xl text-center mb-2">
            {t('analysis.title')}
          </h3>

          <RevealPoint
            emoji="🔴"
            title={t('analysis.qr.title')}
            description={t('analysis.qr.description')}
            delayIndex={0}
          />

          <RevealPoint
            emoji="🔴"
            title={t('analysis.iphone.title')}
            description={t('analysis.iphone.description')}
            delayIndex={1}
          />

          <RevealPoint
            emoji="🔴"
            title={t('analysis.data.title')}
            description={t('analysis.data.description', { nombre, email })}
            delayIndex={2}
          />

          <RevealPoint
            emoji="🔴"
            title={t('analysis.terms.title')}
            description={t('analysis.terms.description')}
            delayIndex={3}
          />

          <RevealPoint
            emoji={reveal3Emoji}
            title={reveal3Title}
            description={reveal3Description}
            delayIndex={4}
          />
        </div>
      </div>

      {/* Section 4: Positive message */}
      <div className="px-6 pb-8">
        <div className="w-full max-w-sm mx-auto glass-card p-6 border-green-500/20">
          <div className="text-3xl mb-3">✅</div>
          <h3 className="text-green-300 font-bold text-lg mb-4">{t('goodNews.title')}</h3>
          <div className="flex flex-col gap-3 text-white/80 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-green-400 flex-shrink-0">→</span>
              <span>
                <Trans i18nKey="goodNews.dataSafe" ns="result" components={{ 1: <strong className="text-white" /> }} />
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-400 flex-shrink-0">→</span>
              <span>{t('goodNews.tip1')}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-400 flex-shrink-0">→</span>
              <span>{t('goodNews.tip2')}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-400 flex-shrink-0">→</span>
              <span>{t('goodNews.tip3')}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-400 flex-shrink-0">→</span>
              <span>
                <Trans
                  i18nKey="goodNews.prize"
                  ns="result"
                  values={{ stand }}
                  components={{ 1: <strong className="text-white" /> }}
                />
              </span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-white/50 text-xs leading-relaxed">
              <Trans i18nKey="goodNews.footer" ns="result" components={{ 1: <strong className="text-white/70" /> }} />
            </p>
          </div>
        </div>
      </div>

      {/* Section 5: CTAs */}
      <div className="px-6 pb-16">
        <div className="w-full max-w-sm mx-auto flex flex-col gap-4">
          <a
            href="https://zepo.app"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-lg rounded-2xl px-8 min-h-[56px] py-4 transition-all active:scale-[0.98] text-center flex items-center justify-center"
          >
            {t('cta')}
          </a>

        </div>
      </div>
    </div>
  );
};

export default Result;
